import {noop, objectFromEntries} from '@benchling/js-utils';
import {useSelector} from '@xstate/react';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {assert, assertAndContinue} from 'Asserts';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  STORABLE_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {isSupportedDocumentTemplateLike} from 'notebook/integrations/plate-maps/FixedPlateEditorUtil';
import {
  EditorPropertiesContext,
  EditorPropertiesContextInterface,
  NoteContext,
  NoteContextInterface,
} from 'notebook/notes/core-ui/NoteContext';
import isConfiguration from 'notebook/util/isConfiguration';
import {Note} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {BaseStorablePaneStudyableFormGroupProps} from 'samples/BaseStorablePaneStudyableFormGroup';
import {CreatePlatePaneState} from 'samples/CreatePlatePane';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import AbandonPlateMapConfirmationModal from 'samples/plate-maps/AbandonPlateMapConfirmationModal';
import {
  ANNOTATE_FILL_PLATE_MODAL_STEPS,
  AnnotateFillPlateModalStep,
  InitiatingPlateDesignState,
  PlateDefinitionType,
  PlateDesignState,
  PlateMapType,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import DefinePlateMapStep from 'samples/plate-maps/annotate-fill-plate-modal/DefinePlateMapStep';
import DefinePlateStep from 'samples/plate-maps/annotate-fill-plate-modal/DefinePlateStep';
import FillPlateTransferStep from 'samples/plate-maps/fill-plate-modal/FillPlateTransferStep';
import useFillPlateModalReducer from 'samples/plate-maps/fill-plate-modal/useFillPlateModalReducer';
import {
  PlateDataParameterContext,
  PlateDataParameterContextType,
} from 'samples/plate-maps/hooks/usePlateDataParameterContext';
import {usePlateMapValidation} from 'samples/plate-maps/hooks/usePlateMapValidation';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {NoPlateMap} from 'samples/plate-maps/NoPlateMap';
import {
  PlateMap,
  PlateMapProcedureContext,
  SchematizedDisplayLayerMetadata,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import {
  fetchContainerSchemaFields,
  getMaterialInputIdsUsedInWell,
  getPlateDataParametersOptions,
  getTestDilutionFactorsForWell,
  shouldShowTestOrderDilutionInfo,
} from 'samples/plate-maps/PlateMapUtil';
import {plateMapHasAnyErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {
  groupedValidationHasAnyBlockingErrors,
  mergeGroupedValidation,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {ValidationErrorsByLayerAndLevel} from 'samples/plate-maps/validation/ValidationTypes';
import {getCreatePlateValidationErrors} from 'samples/StorableModalsUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import StepNav, {StepNavSize, StepNavVariant} from 'ui-library/StepNav';
import {isServerRequestError} from 'util/Net';
import {assertNever, MakeFieldsRequired} from 'util/Types';

import {ConcentrationLayersProps} from '../display-layer-controls/PlateMapDisplayLayerSidebar';
import {FillPlateModalReducerActionType} from '../fill-plate-modal/machine/FillPlateModalConstants';
import {AnnotateFillPlateModalReducerActionType} from './machines/AnnotateFillPlateMachineConstants';
import {
  AnnotateFillPlateProvider,
  useAnnotateFillPlateActorRef,
  useAnnotateFillPlateSelector,
} from './machines/AnnotateFillPlateMachineProvider';

const MAX_PLATE_HEIGHT = 'calc(100vh - 400px)';

export interface AnnotateFillPlateModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  saveNewPlate: ({
    plateMap,
    createPlatePaneState,
    annotatedDataParameterIds,
    annotatedSchemaFieldIds,
    callbackOnError,
  }: {
    plateMap: PlateMap;
    createPlatePaneState: MakeFieldsRequired<CreatePlatePaneState, 'selectedSchema'>;
    annotatedDataParameterIds: Set<string>;
    annotatedSchemaFieldIds: Set<string>;
    callbackOnError: () => void;
  }) => Promise<void>;
  saveExistingPlate: ({
    plateMap,
    grid,
    schemaFieldValues,
    appendStudyIds,
    annotatedSchemaFieldIds,
    callbackOnError,
  }: {
    plateMap: PlateMap;
    grid: FullyAccessibleGrid;
    schemaFieldValues: Array<SchemaTag>;
    appendStudyIds: Array<string>;
    annotatedDataParameterIds: Set<string>;
    annotatedSchemaFieldIds: Set<string>;
    callbackOnError: () => void;
  }) => Promise<void>;
  savePlateDesign: ({
    plateDesignState,
    plateMap,
    annotatedDataParameterIds,
    annotatedSchemaFieldIds,
  }: {
    plateDesignState: PlateDesignState;
    plateMap: PlateMap;
    annotatedDataParameterIds: Set<string>;
    annotatedSchemaFieldIds: Set<string>;
  }) => void;
  procedureContext: PlateMapProcedureContext;
  eventGroupId: string;
  note: Note;
  fileId: string;
  studyableProps: {
    initialStudyIds: Array<string>;
    studySearchInputProps: BaseStorablePaneStudyableFormGroupProps['studySearchInputProps'];
  };
  /**
   * If passed, the modal is being launched from an existing plate design.
   */
  initiatingPlateDesignState?: InitiatingPlateDesignState;
  plateDesignNames: Array<string>;
}

function getTooltipForUnresolvedValidationErrors({
  hasPlateMapStepErrors,
  hasTransferStepErrors,
  stepKey,
}: {
  hasPlateMapStepErrors: boolean;
  hasTransferStepErrors: boolean;
  stepKey: AnnotateFillPlateModalStep;
}): string | null {
  if (stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE_MAP && hasPlateMapStepErrors) {
    return 'You must resolve all errors to proceed';
  } else if (stepKey === AnnotateFillPlateModalStep.TRANSFER_QUANTITIES) {
    if (hasPlateMapStepErrors) {
      return 'Previous step has errors, go back to resolve';
    } else if (hasTransferStepErrors) {
      return 'You must resolve all errors to proceed';
    }
  }
  return null;
}

function getNextButtonTooltipTitle({
  cannotValidate,
  hasPlateMapStepErrors,
  hasTransferStepErrors,
  stepKey,
  validationStatus,
}: {
  cannotValidate: boolean;
  hasPlateMapStepErrors: boolean;
  hasTransferStepErrors: boolean;
  stepKey: AnnotateFillPlateModalStep;
  validationStatus: AsyncStatus;
}): string | null {
  if (!cannotValidate) {
    if (validationStatus === AsyncStatus.SUCCESS) {
      return getTooltipForUnresolvedValidationErrors({hasPlateMapStepErrors, hasTransferStepErrors, stepKey});
    } else if (validationStatus === AsyncStatus.NOT_FETCHED || validationStatus === AsyncStatus.LOADING) {
      return 'Validating...';
    } else {
      return 'Validation failed, please retry';
    }
  }
  return null;
}

function getModalButtons({
  isTemplateContext,
  stepKey,
  setStepKey,
  hasDefinedPlate,
  plateMap,
  plateMapType,
  validationErrors,
  validationStatus,
}: {
  isTemplateContext: boolean;
  stepKey: AnnotateFillPlateModalStep;
  setStepKey: (newKey: AnnotateFillPlateModalStep) => void;
  hasDefinedPlate: boolean;
  plateMap: PlateMap | null;
  plateMapType: PlateMapType | null;
  validationErrors: ValidationErrorsByLayerAndLevel | null;
  validationStatus: AsyncStatus;
}): Array<ModalButton> {
  const cannotValidate =
    plateMap == null || validationErrors == null || validationStatus !== AsyncStatus.SUCCESS;
  const hasPlateMapStepErrors = !cannotValidate && groupedValidationHasAnyBlockingErrors(validationErrors);
  const hasTransferStepErrors = !cannotValidate && plateMapHasAnyErrors(plateMap);

  const createSubmitButton = (): ModalButton => {
    let disableProgress = cannotValidate || hasPlateMapStepErrors || hasTransferStepErrors;
    let nextButtonText;
    switch (plateMapType) {
      case PlateMapType.DESIGN:
        nextButtonText = 'Save design';
        break;
      case PlateMapType.RECORD:
        nextButtonText = 'Fill';
        break;
      case null:
        assertAndContinue(
          false,
          'Expected plate map type to be defined before reaching TRANSFER_QUANTITIES step.'
        );
        // User may accidentally land here if step nav is not properly disabled.
        // Avoid hard-erroring since the submit button is disabled: the user can
        // navigate back to the first step to fix the issue.
        nextButtonText = 'Create';
        disableProgress = true;
        break;
      default:
        throw new Error(`Unexpected plate map type: ${assertNever(plateMapType)}`);
    }
    return {
      text: nextButtonText,
      submit: true,
      disabled: disableProgress,
      tooltipTitle: getNextButtonTooltipTitle({
        cannotValidate,
        hasTransferStepErrors,
        hasPlateMapStepErrors,
        stepKey,
        validationStatus,
      }),
    };
  };

  const buttons: Array<ModalButton> = [];
  if (stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE) {
    buttons.push({text: 'Cancel', cancel: true});
    buttons.push({
      text: 'Next',
      submit: true,
      disabled: !hasDefinedPlate,
      dataTestAttrs: {component: 'AnnotateFillPlateModal', key: ModalButtonKey.NEXT},
    });
  } else if (stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE_MAP) {
    buttons.push({
      text: 'Back',
      onClick: () => {
        setStepKey(AnnotateFillPlateModalStep.DEFINE_PLATE);
      },
    });

    if (isTemplateContext) {
      buttons.push(createSubmitButton());
    } else {
      buttons.push({
        text: 'Next',
        submit: true,
        disabled: cannotValidate || hasPlateMapStepErrors,
        dataTestAttrs: {component: 'AnnotateFillPlateModal', key: ModalButtonKey.NEXT},
        tooltipTitle: getNextButtonTooltipTitle({
          cannotValidate,
          hasTransferStepErrors,
          hasPlateMapStepErrors,
          stepKey,
          validationStatus,
        }),
      });
    }
  } else if (stepKey === AnnotateFillPlateModalStep.TRANSFER_QUANTITIES) {
    buttons.push({
      text: 'Back',
      onClick: () => {
        setStepKey(AnnotateFillPlateModalStep.DEFINE_PLATE_MAP);
      },
    });
    buttons.push(createSubmitButton());
  }
  return buttons;
}

function getModalTitle(plateMapType: PlateMapType | null, isFromExistingPlateDesign: boolean): string {
  switch (plateMapType) {
    case PlateMapType.DESIGN:
      return `${isFromExistingPlateDesign ? 'Edit' : 'Create'} plate design`;
    case PlateMapType.RECORD:
      return `Create plate record${isFromExistingPlateDesign ? ' from plate design' : ''}`;
    case null:
      return `Create plate map`;
    default:
      throw new Error(`Unexpected plate map type: ${assertNever(plateMapType)}`);
  }
}

function getLeaveModalText(
  plateMapType: PlateMapType | null,
  isFromExistingPlateDesign: boolean
): {title: string; confirmButtonText: string} {
  switch (plateMapType) {
    case PlateMapType.DESIGN:
      if (isFromExistingPlateDesign) {
        return {
          title: 'Leave plate design edit?',
          confirmButtonText: 'Continue editing',
        };
      }
      return {
        title: 'Leave plate design creation?',
        confirmButtonText: 'Continue creating',
      };
    case PlateMapType.RECORD:
      return {
        title: 'Leave plate record creation?',
        confirmButtonText: 'Continue creating',
      };
    case null:
      return {
        title: 'Leave plate map?',
        confirmButtonText: 'Continue editing',
      };
    default:
      throw new Error(`Unexpected plate map type: ${assertNever(plateMapType)}`);
  }
}

/**
 * Modal with the steps to create a new plate or edit an existing plate,
 * and annotate a plate and/or fill it with entities.
 */
export default function AnnotateFillPlateModal(props: AnnotateFillPlateModalProps): JSX.Element {
  return (
    <AnnotateFillPlateProvider
      note={props.note}
      studyableProps={props.studyableProps}
      fileId={props.fileId}
      procedureContext={props.procedureContext}
      existingPlateDesignNames={props.plateDesignNames}
      initialStudyIds={props.studyableProps.initialStudyIds}
      initiatingPlateDesignState={props.initiatingPlateDesignState}
    >
      <ProvidedAnnotateFillPlateModal
        eventGroupId={props.eventGroupId}
        onRequestClose={props.onRequestClose}
        onRequestSubmit={props.onRequestSubmit}
        saveExistingPlate={props.saveExistingPlate}
        saveNewPlate={props.saveNewPlate}
        savePlateDesign={props.savePlateDesign}
      />
    </AnnotateFillPlateProvider>
  );
}

function ProvidedAnnotateFillPlateModal(
  props: Pick<
    AnnotateFillPlateModalProps,
    | 'onRequestClose'
    | 'onRequestSubmit'
    | 'eventGroupId'
    | 'saveExistingPlate'
    | 'saveNewPlate'
    | 'savePlateDesign'
  >
): JSX.Element {
  const modalSteps = ANNOTATE_FILL_PLATE_MODAL_STEPS;
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    initialPlateMap,
    containerSchemaId,
    stepKey,
    statusByStepKey,
    plateMapType,
    plateDefinitionType,
    plateDesignState,
    createPlatePaneState,
    existingPlateState,
    selectedGrid,
    dismissablePlateMapValidation,
    plateDataParameters,
    isFromExistingPlateDesign,
    selectedPlateDataParameterIds: annotateSelectedPlateDataParameterIds,
    procedureContext,
    fileId,
    initiatingPlateDesignState,
    studyableProps,
    existingPlateDesignNames,
    disabledSections,
    allowPlateDesigns,
    allowPlateRecords,
  } = useAnnotateFillPlateSelector((snap) => snap.context);

  const noteContext = useAnnotateFillPlateSelector(
    (snap) => ({note: snap.context.note}) as NoteContextInterface,
    (a, b) => a.note === b.note
  );

  const {isInProcedure} = procedureContext;

  const actorRef = useAnnotateFillPlateActorRef();

  // Get concentration layer machine actor ref for event forwarding
  const concentrationLayerMachineActor = useAnnotateFillPlateSelector(
    (snap) => snap.context.concentrationLayerMachine
  );
  // Directly subscribe to the concentration layer machine state for immediate updates
  const concentrationLayerMachineState = useSelector(
    concentrationLayerMachineActor,
    (state) => state,
    (a, b) => a === b
  );

  const fetchTestOrders = shouldShowTestOrderDilutionInfo(
    procedureContext.procedureStudyDesignType,
    plateMapType
  );

  const {plateMap: plateMapResult, isPending} = usePlateMapFromGrid(selectedGrid, fetchTestOrders);

  useEffect(() => {
    if (
      plateMapType === PlateMapType.RECORD &&
      plateDefinitionType === PlateDefinitionType.EXISTING &&
      plateMapResult?.id !== initialPlateMap?.id &&
      !isPending
    ) {
      actorRef.send({
        type: AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP,
        updatedPlateMapInfo:
          plateMapResult != null && selectedGrid != null
            ? {
                plateMap: plateMapResult,
                containerSchemaId: selectedGrid.storableSchema.containerSchemaId!,
              }
            : null,
        isInProcedure,
        plateDataParameters,
      });
    }
  }, [
    plateMapResult?.id,
    initialPlateMap,
    actorRef,
    plateMapType,
    plateDefinitionType,
    selectedGrid,
    isInProcedure,
    isPending,
    plateMapResult,
    plateDataParameters,
  ]);

  const [fillPlateState, dispatchFillPlateModalReducer] = useFillPlateModalReducer({
    isInProcedure: procedureContext.isInProcedure,
    plateMap: initialPlateMap,
    selectedPlateDataParameterIds: annotateSelectedPlateDataParameterIds,
  });

  const [hasSeenTransferStep, setHasSeenTransferStep] = useState(false);
  const [passedDefinePlateStep, setPassedDefinePlateStep] = useState(false);

  useEffect(() => {
    if (
      (procedureContext.plateMaterialInputs != null && procedureContext.plateMaterialInputs.length > 0) ||
      (plateDataParameters != null && plateDataParameters.length > 0)
    ) {
      dispatchFillPlateModalReducer({
        type: FillPlateModalReducerActionType.INITIALIZE_PROCEDURE_DATA,
        plateDataParameters: plateDataParameters ?? [],
        // If we came into the modal with data parameters selected, don't clobber them. If we didn't, go ahead
        // and calculate.
        calculateInitialDataParameters: annotateSelectedPlateDataParameterIds == null,
        plateMaterialInputsById: objectFromEntries(
          (procedureContext.plateMaterialInputs ?? []).map((plateMaterialInput) => [
            plateMaterialInput.versionId,
            plateMaterialInput,
          ])
        ),
      });
    }
    // We only want to do this when parameters or material inputs change.
    // dispatchFillPlateModalReducer _shouldn't_ change across renders - but better safe than sorry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plateDataParameters, procedureContext.plateMaterialInputs]);

  const onLoadWellSchemaFields = useCallback((schemaFieldLayers: Array<SchematizedDisplayLayerMetadata>) => {
    dispatchFillPlateModalReducer({
      type: FillPlateModalReducerActionType.SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS,
      schemaFieldLayers,
      initialSelectedLayerIds: initiatingPlateDesignState?.wellSchemaOptions
        ? initiatingPlateDesignState.wellSchemaOptions.map(({attribute}) => attribute.key)
        : null,
    });
    // We don't want this function handle to update. dispatchFillPlateModalReducer _shouldn't_ change across
    // renders - but better safe than sorry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wellSchemaLayers = useWellSchemaLayers(
    containerSchemaId,
    onLoadWellSchemaFields,
    initiatingPlateDesignState?.wellSchemaOptions ?? []
  );

  const plateDataParameterContextMemo = useMemo<PlateDataParameterContextType>(
    () => ({
      plateDataParameters,
    }),
    [plateDataParameters]
  );

  const editorPropertiesMemo = useMemo<EditorPropertiesContextInterface>(
    () => ({
      fileId,
      showFormulas: false,
      isConfig: isConfiguration(fileId),
    }),
    [fileId]
  );

  const {
    plateMap,
    reagentGroupsById,
    selectedWellIndices,
    selectedPlateDataParameterIds,
    selectedWellSchemaFieldIds,
    pagination,
    viewMode,
  } = fillPlateState;

  const shouldPreviewWellRoles = useMemo(
    () =>
      hasSeenTransferStep || isFromExistingPlateDesign
        ? false
        : (fillPlateState.plateMap?.wells.some((well) => well.role != null) ?? false),
    [hasSeenTransferStep, isFromExistingPlateDesign, fillPlateState.plateMap]
  );

  const plateSchemaId =
    (plateMapType === PlateMapType.DESIGN
      ? plateDesignState.plateSchema?.id
      : plateDefinitionType === PlateDefinitionType.CREATE
        ? createPlatePaneState.selectedSchema?.id
        : selectedGrid?.storableSchema?.id) ?? null;
  const {refetch, validationStatus, validationRequestFailedMessage, validatedPlateMap, validationErrors} =
    usePlateMapValidation({
      plateId: selectedGrid?.id ?? null,
      plateMap: plateMap ?? null,
      annotatedSchemaFieldIds: selectedWellSchemaFieldIds,
      annotatedDataParameterIds: selectedPlateDataParameterIds,
      plateSchemaId,
      documentId: fileId,
    });

  const groupedErrors = mergeGroupedValidation(dismissablePlateMapValidation, validationErrors ?? {});

  const analyticsMetadata = {
    actionOrigin: procedureContext.isInProcedure ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK,
    modalType: FixedPlateModalType.FIXED_PLATE_EDITOR,
    plateId: plateMap?.id,
    plateMapType,
    eventGroupId: props.eventGroupId,
    studyIds: studyableProps.initialStudyIds,
  };

  const duplicatePlateDesignName = existingPlateDesignNames.includes((plateDesignState.name ?? '').trim());

  function hasDefinedPlate(): boolean {
    // If we have moved to any step after DEFINE_PLATE, then the plate map
    // definition will be reflected in plateMap.
    if (stepKey !== AnnotateFillPlateModalStep.DEFINE_PLATE) {
      return plateMap != null;
    }
    // Otherwise, check all blocking fields in DEFINE_PLATE step are filled out.
    switch (plateMapType) {
      case PlateMapType.DESIGN:
        return plateDesignState.plateSchema != null && !duplicatePlateDesignName;
      case PlateMapType.RECORD: {
        switch (plateDefinitionType) {
          case PlateDefinitionType.CREATE:
            return Object.keys(getCreatePlateValidationErrors(createPlatePaneState)).length === 0;
          case PlateDefinitionType.EXISTING:
            return (
              existingPlateState.selectedGridId != null &&
              !existingPlateState.isValidating &&
              existingPlateState.error == null
            );
          default:
            throw new Error(`Unexpected plate definition type: ${assertNever(plateDefinitionType)}`);
        }
      }
      case null:
        return false;
      default:
        throw new Error(`Unexpected plate map type: ${assertNever(plateMapType)}`);
    }
  }

  async function onSetStepKey(newStepKey: AnnotateFillPlateModalStep): Promise<void> {
    analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.NAVIGATED_STEP_COMPONENT,
      ANALYTICS_MODULE.SAMPLES,
      {...analyticsMetadata, stepKey: newStepKey, previousStepKey: stepKey}
    );
    actorRef.send({
      type: AnnotateFillPlateModalReducerActionType.UPDATE_STEP_KEY,
      stepKey: newStepKey,
    });
    if (
      stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE &&
      newStepKey === AnnotateFillPlateModalStep.DEFINE_PLATE_MAP &&
      (plateMapType === PlateMapType.DESIGN ||
        (plateDefinitionType === PlateDefinitionType.CREATE && !isFromExistingPlateDesign))
    ) {
      const selectedContainerSchemaId =
        plateMapType === PlateMapType.DESIGN
          ? plateDesignState.plateSchema?.containerSchemaId
          : createPlatePaneState.selectedSchema?.containerSchemaId;
      assert(selectedContainerSchemaId != null, 'Expected container schema to be defined.', {
        plateMapType,
        plateDefinitionType,
      });
      const containerSchemaFields = await fetchContainerSchemaFields(selectedContainerSchemaId);
      actorRef.send({
        type: AnnotateFillPlateModalReducerActionType.CREATE_NEW_PLATE_MAP,
        containerSchemaId: selectedContainerSchemaId,
        containerSchemaFields,
        plateDataParameters,
      });
    }

    if (newStepKey === AnnotateFillPlateModalStep.TRANSFER_QUANTITIES) {
      setHasSeenTransferStep(true);
    }
    if (
      stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE &&
      newStepKey !== AnnotateFillPlateModalStep.DEFINE_PLATE
    ) {
      setPassedDefinePlateStep(true);
    }
  }

  async function handleSubmit(): Promise<void> {
    const isLastStep =
      stepKey === AnnotateFillPlateModalStep.TRANSFER_QUANTITIES ||
      (isTemplateContext && stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE_MAP);

    if (!isLastStep) {
      const stepIndex = modalSteps.findIndex((step) => step.key === stepKey);
      await onSetStepKey(modalSteps[stepIndex + 1].key);
    } else {
      assert(plateMap != null, 'Expected plateMap to be defined before submitting.');
      assert(plateMapType != null, 'Expected plateMapType to be defined before submitting.');
      analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.SAMPLES, {
        ...analyticsMetadata,
        numDataParametersUsed: new Set<string>(
          plateMap.wells.flatMap(({dataParameterTags}) => Object.keys(dataParameterTags))
        ).size,
        numMaterialInputsUsed: new Set<string>(
          plateMap.wells.flatMap((well) => getMaterialInputIdsUsedInWell(well))
        ).size,
        numDistinctDilutionFactors: new Set<number>(
          plateMap.wells.flatMap((well) =>
            [...getTestDilutionFactorsForWell(well, plateMap.testOrderDilutionInfoById)].filter(
              (dilutionFactor): dilutionFactor is number => typeof dilutionFactor === 'number'
            )
          )
        ).size,
      });
      try {
        switch (plateMapType) {
          case PlateMapType.RECORD:
            if (plateDefinitionType === PlateDefinitionType.CREATE) {
              assert(
                createPlatePaneState.selectedSchema != null,
                'Expected schema to be defined before submitting.'
              );
              await props.saveNewPlate({
                plateMap,
                createPlatePaneState: createPlatePaneState as MakeFieldsRequired<
                  CreatePlatePaneState,
                  'selectedSchema'
                >,
                annotatedDataParameterIds: selectedPlateDataParameterIds,
                annotatedSchemaFieldIds: selectedWellSchemaFieldIds,
                callbackOnError: refetch ?? noop,
              });
            } else {
              await props.saveExistingPlate({
                plateMap,
                grid: selectedGrid!,
                schemaFieldValues: existingPlateState.tags,
                appendStudyIds: existingPlateState.appendStudyIds,
                annotatedDataParameterIds: selectedPlateDataParameterIds,
                annotatedSchemaFieldIds: selectedWellSchemaFieldIds,
                callbackOnError: refetch ?? noop,
              });
            }
            break;
          case PlateMapType.DESIGN:
            // Design is inserted synchronously into the notebook content, relying on auto-save,
            // so we don't need to wait for a promise to resolve.
            props.savePlateDesign({
              plateDesignState,
              plateMap,
              annotatedDataParameterIds: selectedPlateDataParameterIds,
              annotatedSchemaFieldIds: selectedWellSchemaFieldIds,
            });
            break;
          default:
            throw new Error(`Unexpected plate map type: ${assertNever(plateMapType)}`);
        }
        props.onRequestSubmit();
      } catch (e) {
        if (isServerRequestError(e)) {
          notifications.notifyError(e);
          return;
        }
        throw e;
      }
    }
  }

  // If a user removes a selected layer when editing an existing plate, we want to revert the values on that
  // layer to the initial values so that popovers continue to reflect the initial state of the existing plate.
  // We only do this for plate records because when using a plate design to edit an existing plate, the
  // initial plate map represents the plate design, not the target plate.
  // NOTE: We only do this for schema fields and not data parameters because we don't currently ever load the
  // current data parameters for an existing plate when launching the modal.
  const revertLayersToInitialValuesOnDelete =
    plateMapType === PlateMapType.RECORD && plateDefinitionType === PlateDefinitionType.EXISTING;

  // PlateMapType is expected to be defined when the plate map is defined in the define plate map step.
  const safeGetPlateMapType = (): PlateMapType => {
    assert(plateMapType != null, 'Expected plateMapType to be defined.');
    return plateMapType;
  };

  const isTemplateContext = isSupportedDocumentTemplateLike({
    fileId,
    isConfig: isConfiguration(fileId),
  });

  const concentrationLayersProps: ConcentrationLayersProps = useMemo(() => {
    assert(
      concentrationLayerMachineState != null && concentrationLayerMachineActor != null,
      'Expected concentration layer machine state and actor ref to be defined.'
    );
    const {layers, orderedLayerIds, activeLayerId} = concentrationLayerMachineState.context;
    const concentrationLayers = orderedLayerIds.map((layerId) => layers.get(layerId)!);

    return {
      enableAddRemoveLayers: true,
      concentrationLayers,
      activeLayerId,
      onAddLayer: () => {
        concentrationLayerMachineActor.send({
          type: 'addLayer',
        });
      },
      onRemoveLayer: (layerId: string) => {
        concentrationLayerMachineActor.send({
          type: 'removeLayer',
          layerId,
        });
      },
    };
  }, [concentrationLayerMachineState, concentrationLayerMachineActor]);

  let steps = ANNOTATE_FILL_PLATE_MODAL_STEPS;

  if (isTemplateContext) {
    steps = steps.filter((s) => s.key !== AnnotateFillPlateModalStep.TRANSFER_QUANTITIES);
  }

  return (
    <Modal
      title={getModalTitle(plateMapType, isFromExistingPlateDesign)}
      onRequestClose={() => {
        if (!isEqual(plateMap, initialPlateMap)) {
          runModal(AbandonPlateMapConfirmationModal, {
            onRequestClose: noop,
            onRequestSubmit: props.onRequestClose,
            ...getLeaveModalText(plateMapType, isFromExistingPlateDesign),
          });
        } else {
          props.onRequestClose();
        }
      }}
      onRequestSubmit={handleSubmit}
      fullWidth={stepKey !== AnnotateFillPlateModalStep.DEFINE_PLATE}
      mediumLarge={stepKey === AnnotateFillPlateModalStep.DEFINE_PLATE}
      expandHeight={true}
      buttons={getModalButtons({
        isTemplateContext,
        stepKey,
        setStepKey: onSetStepKey,
        hasDefinedPlate: hasDefinedPlate(),
        plateMap: validatedPlateMap,
        plateMapType,
        validationErrors: groupedErrors,
        validationStatus,
      })}
      wrapInForm={false}
      closeableOnEscapeKey={false}
      ref={modalRef}
    >
      <StepNav<AnnotateFillPlateModalStep>
        currentStepKey={stepKey}
        onUpdateCurrentStep={onSetStepKey}
        steps={steps}
        statusByStep={statusByStepKey}
        size={StepNavSize.SMALL}
        variant={StepNavVariant.FLAT}
      />
      <hr className={css(styles.horizontalLine)} />
      {(() => {
        switch (stepKey) {
          case AnnotateFillPlateModalStep.DEFINE_PLATE:
            return (
              <DefinePlateStep
                createPlatePaneState={createPlatePaneState}
                setCreatePlatePaneState={(updatedState) => {
                  actorRef.send({
                    type: AnnotateFillPlateModalReducerActionType.UPDATE_CREATE_PLATE_PANE_STATE,
                    createPlatePaneState: updatedState,
                  });
                }}
                existingPlateState={existingPlateState}
                setExistingPlateState={(updatedState) => {
                  actorRef.send({
                    type: AnnotateFillPlateModalReducerActionType.UPDATE_EXISTING_PLATE_STATE,
                    existingPlateState: updatedState,
                  });
                }}
                plateDefinitionType={plateDefinitionType}
                setPlateDefinitionType={(type) => {
                  analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.TOGGLED_PLATE_DEFINITION_TYPE, {
                    ...analyticsMetadata,
                    definitionType: type,
                  });
                  actorRef.send({
                    type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DEFINITION_TYPE,
                    plateDefinitionType: type,
                  });
                }}
                plateMapType={plateMapType}
                setPlateMapType={(type) => {
                  actorRef.send({
                    type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_MAP_TYPE,
                    plateMapType: type,
                  });
                }}
                plateDesignState={plateDesignState}
                setPlateDesignState={(state) => {
                  actorRef.send({
                    type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DESIGN_STATE,
                    plateDesignState: state,
                  });
                }}
                isFromExistingPlateDesign={isFromExistingPlateDesign}
                studySearchInputProps={studyableProps.studySearchInputProps}
                showChangeWarning={passedDefinePlateStep}
                duplicatePlateDesignName={duplicatePlateDesignName}
                allowPlateDesigns={allowPlateDesigns}
                allowPlateRecords={allowPlateRecords}
              />
            );
          case AnnotateFillPlateModalStep.DEFINE_PLATE_MAP:
            return initialPlateMap != null && validatedPlateMap != null ? (
              <PlateDataParameterContext.Provider value={plateDataParameterContextMemo}>
                <EditorPropertiesContext.Provider value={editorPropertiesMemo}>
                  <NoteContext.Provider value={noteContext}>
                    <DefinePlateMapStep
                      key={stepKey}
                      plateMap={validatedPlateMap}
                      setPlateMap={(plateMap) => {
                        dispatchFillPlateModalReducer({
                          type: FillPlateModalReducerActionType.UPDATE_PLATE_MAP_ANNOTATIONS,
                          plateMap,
                        });
                      }}
                      initialPlateMap={initialPlateMap}
                      initialSelectedWellIndices={selectedWellIndices}
                      initialSelectedAttribute={WELL_ROLE_ATTRIBUTE_SENTINEL}
                      modalRef={modalRef}
                      // Re-add plateId to the metadata to prove to typescript plateMap is not null.
                      analyticsMetadata={{...analyticsMetadata, plateId: validatedPlateMap.id}}
                      procedureContext={procedureContext}
                      selectedPlateDataParameterIds={selectedPlateDataParameterIds}
                      plateDataParameters={plateDataParameters}
                      maxPlateViewHeight={MAX_PLATE_HEIGHT}
                      fillPlateDispatch={dispatchFillPlateModalReducer}
                      fillPlateState={fillPlateState}
                      disabledSections={disabledSections}
                      showFinalMeasurements={true}
                      plateMapType={safeGetPlateMapType()}
                      selectedWellSchemaFieldIds={selectedWellSchemaFieldIds}
                      wellSchemaLayers={wellSchemaLayers}
                      onAddSelectedDataParameter={(parameterVersionId) => {
                        analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.ADDED_ANNOTATION_LAYER, {
                          ...analyticsMetadata,
                          layerType: 'parameter',
                        });
                        dispatchFillPlateModalReducer({
                          type: FillPlateModalReducerActionType.ADD_SELECTED_PLATE_DATA_PARAMETER,
                          parameterVersionId,
                        });
                      }}
                      onRemoveSelectedDataParameter={(parameterVersionId) => {
                        analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.REMOVED_ANNOTATION_LATER, {
                          ...analyticsMetadata,
                          layerType: 'parameter',
                        });
                        dispatchFillPlateModalReducer({
                          type: FillPlateModalReducerActionType.REMOVE_SELECTED_PLATE_DATA_PARAMETER,
                          parameterVersionId,
                        });
                      }}
                      onAddSelectedSchemaField={(schemaFieldId) => {
                        dispatchFillPlateModalReducer({
                          type: FillPlateModalReducerActionType.ADD_SELECTED_WELL_SCHEMA_FIELD,
                          schemaFieldId,
                        });
                      }}
                      onRemoveSelectedSchemaField={(schemaFieldId) => {
                        dispatchFillPlateModalReducer({
                          type: FillPlateModalReducerActionType.REMOVE_SELECTED_WELL_SCHEMA_FIELD,
                          schemaFieldId,
                          tagsToRevertTo: revertLayersToInitialValuesOnDelete
                            ? initialPlateMap.wells.map((well) => well.schemaTags[schemaFieldId])
                            : null,
                        });
                      }}
                      initialShouldPreviewWellRoles={shouldPreviewWellRoles}
                      validationErrors={groupedErrors}
                      onDismissErrors={(errorUuids: Set<string>) => {
                        actorRef.send({
                          type: AnnotateFillPlateModalReducerActionType.DISMISS_VALIDATION_ERROR,
                          errorUuids,
                        });
                      }}
                      concentrationLayersProps={concentrationLayersProps}
                    />
                  </NoteContext.Provider>
                </EditorPropertiesContext.Provider>
              </PlateDataParameterContext.Provider>
            ) : (
              <NoPlateMap
                refetch={refetch}
                validationStatus={validationStatus}
                validationRequestFailedMessage={validationRequestFailedMessage}
              />
            );
          case AnnotateFillPlateModalStep.TRANSFER_QUANTITIES:
            return validatedPlateMap != null ? (
              <FillPlateTransferStep
                dispatch={dispatchFillPlateModalReducer}
                pagination={pagination}
                plateMap={validatedPlateMap}
                plateMaterialInputs={procedureContext.plateMaterialInputs}
                reagentGroupsById={reagentGroupsById}
                selectedWellIndices={selectedWellIndices}
                viewMode={viewMode}
                modalRef={modalRef}
                // Re-add plateId to the metadata to prove to typescript plateMap is not null.
                analyticsMetadata={{...analyticsMetadata, plateId: validatedPlateMap.id}}
                maxPlateViewHeight={MAX_PLATE_HEIGHT}
                procedureStudyDesignType={procedureContext.procedureStudyDesignType}
                plateMapType={safeGetPlateMapType()}
                plateDataParametersOptions={getPlateDataParametersOptions(plateDataParameters ?? [])}
                containerSchemaId={containerSchemaId!}
              />
            ) : (
              <NoPlateMap
                refetch={refetch}
                validationStatus={validationStatus}
                validationRequestFailedMessage={validationRequestFailedMessage}
              />
            );
          default:
            throw new Error(`Unexpected step key: ${assertNever(stepKey)}`);
        }
      })()}
    </Modal>
  );
}

const styles = StyleSheet.create({
  horizontalLine: {
    // Account for padding of parent modal; this should stretch across the entire width of the modal body
    marginLeft: '-16px',
    marginRight: '-16px',
  },
});
